"use client";

import { Ellipsis } from "lucide-react";
import { usePathname } from "next/navigation";
import { useEffect, useId, useRef, useState, type ReactNode } from "react";

import { Button } from "./Button";

/**
 * A page heading's actions: the row they have always been, except on a phone
 * (below `sm`), where two or more of them fold behind one button, as a menu
 * dropped from it. A lone action stays in place — there is nothing to fold —
 * and so does an empty set. The fold is CSS (the `folded` variant,
 * globals.css) over one panel, so the controls render once wherever the
 * viewport is, and a dialog one of them opens — a delete's confirm, a
 * reservation's cancel — stays a descendant of a panel that stays shown
 * until the menu is closed. How many actions there are is read from the DOM,
 * so one a role cannot use, rendered as nothing, does not count.
 */
export function PageActions({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  const pathname = usePathname();

  // A link in the menu navigates; the menu goes with the page. (A one-shot
  // sync to navigation — not the cascading-render case the lint rule guards
  // against.)
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setOpen(false);
  }, [pathname]);

  // A press anywhere else closes it, without taking focus from what was
  // pressed. A dialog an action opened is inside the root (the house dialogs
  // are inline, no portal), so a press in one leaves the menu — and with it
  // the dialog — in place.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  return (
    <div
      ref={rootRef}
      className="page-actions relative"
      onKeyDown={(e) => {
        if (e.key !== "Escape" || !open) return;
        // An open dialog's Escape is its own: it closes the dialog, and the
        // menu under it stays shown.
        if ((e.target as Element).closest("dialog[open]")) return;
        setOpen(false);
        triggerRef.current?.focus();
      }}
    >
      <span className="hidden folded:flex">
        <Button
          ref={triggerRef}
          aria-label="actions"
          aria-expanded={open}
          aria-controls={panelId}
          title={open ? "Hide this page's actions" : "Show this page's actions"}
          onClick={() => setOpen((o) => !o)}
        >
          <Ellipsis aria-hidden className="size-4" />
        </Button>
      </span>
      <div
        id={panelId}
        // Folded, it drops from the button's corner: a settle, not a drop.
        className={`page-actions-panel flex flex-wrap items-center gap-3 folded:absolute folded:top-full folded:right-0 folded:z-50 folded:mt-1 folded:min-w-48 folded:origin-top-right folded:flex-col folded:items-start folded:rounded-lg folded:bg-bg folded:p-3 folded:shadow-md folded:ring-1 folded:ring-fg/10 folded:transition-[opacity,translate] folded:duration-100 folded:ease-out folded:starting:-translate-y-1 folded:starting:opacity-0 dark:folded:bg-bg-muted ${
          open ? "" : "folded:hidden"
        }`}
      >
        {children}
      </div>
    </div>
  );
}
